import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles } from "lucide-react";
import {
    requestSuggestion,
    isRateLimited,
    secondsUntilNextTap,
} from "@/lib/mochiPick";

// Motion props as a module constant — referentially stable across renders.
const PICKER_MOTION = {
    initial: { opacity: 0, y: -4, scale: 0.96 },
    animate: { opacity: 1, y: 0, scale: 1 },
    exit: { opacity: 0, y: -4, scale: 0.96 },
    transition: { type: "spring", stiffness: 280, damping: 22 },
};

/**
 * SuggestButton — "what should I do?" pill that, on tap, opens a 3-button
 * inline energy picker. Once an energy is picked, fires the suggestion call
 * and bubbles the parsed response up via `onPick` / `onLoading`.
 *
 *   <SuggestButton onPick={(data) => ...} onLoading={(bool) => ...} />
 *
 * Spec rules enforced here:
 *   - 5-min rate limit (disabled state shows the cooldown timer)
 *   - Loading state delegated to parent (parent updates Mochi's bubble)
 *   - 8-sec timeout + soft fallback message all handled in lib/mochiPick.js
 */
export default function SuggestButton({ onPick, onLoading }) {
    const [open, setOpen] = useState(false);
    const [sending, setSending] = useState(false);
    const [tick, setTick] = useState(0); // forces cooldown re-render

    // Re-render every 1s while the rate limit is active so the countdown updates.
    useEffect(() => {
        const id = setInterval(() => setTick((t) => t + 1), 1000);
        return () => clearInterval(id);
    }, []);

    const cooldownSec = secondsUntilNextTap();
    const limited = isRateLimited();
    void tick;

    const handlePick = async (energy) => {
        setOpen(false);
        setSending(true);
        onLoading?.(true);
        try {
            const data = await requestSuggestion(energy);
            onPick?.(data);
        } finally {
            setSending(false);
            onLoading?.(false);
        }
    };

    return (
        <div className="relative inline-block">
            <button
                type="button"
                disabled={limited || sending}
                onClick={() => setOpen((v) => !v)}
                className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 font-display text-xs transition-all ${
                    limited || sending
                        ? "bg-surface text-warm-500 cursor-not-allowed"
                        : "bg-mustard-soft text-warm-900 hover:bg-mustard hover:-translate-y-0.5 shadow-clay"
                }`}
                data-testid="mochi-suggest-btn"
                aria-label="Ask Mochi what to do"
            >
                <Sparkles size={12} />
                {sending
                    ? "thinking…"
                    : limited
                    ? `next pick in ${Math.floor(cooldownSec / 60)}:${String(cooldownSec % 60).padStart(2, "0")}`
                    : "what should I do? 🍡"}
            </button>

            <AnimatePresence>
                {open && !limited && !sending && (
                    <motion.div
                        {...PICKER_MOTION}
                        className="absolute left-0 top-full mt-2 bg-cream rounded-2xl shadow-clay border border-warm-300/40 p-2 z-40"
                        data-testid="mochi-energy-picker"
                    >
                        <p className="font-display text-[10px] uppercase tracking-widest text-warm-500 px-1 mb-1.5 whitespace-nowrap">
                            how's your energy?
                        </p>
                        <div className="flex gap-1.5">
                            <button
                                onClick={() => handlePick(1)}
                                className="px-2.5 py-1.5 rounded-full bg-surface hover:bg-surface-hover font-body text-xs text-warm-800 whitespace-nowrap"
                                data-testid="mochi-energy-low"
                            >
                                low 🌙
                            </button>
                            <button
                                onClick={() => handlePick(3)}
                                className="px-2.5 py-1.5 rounded-full bg-surface hover:bg-surface-hover font-body text-xs text-warm-800 whitespace-nowrap"
                                data-testid="mochi-energy-med"
                            >
                                meh 🌿
                            </button>
                            <button
                                onClick={() => handlePick(5)}
                                className="px-2.5 py-1.5 rounded-full bg-surface hover:bg-surface-hover font-body text-xs text-warm-800 whitespace-nowrap"
                                data-testid="mochi-energy-high"
                            >
                                high ⚡
                            </button>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}
